/**
 * PatternInsights component - Enhanced pattern analysis with correlations and optimal ranges.
 *
 * Material Design themed displaying:
 * - Pattern detection insights
 * - Statistical correlations between metrics
 * - Optimal ranges for high-performance days
 */

import React from 'react';
import { PatternInsight } from '../../types';
import { CorrelationInsight, OptimalRange } from '../../types/progress';
import CorrelationScatterPlot from './CorrelationScatterPlot';

interface PatternInsightsProps {
  patterns: PatternInsight[];
  dataPoints: number;
  correlations?: CorrelationInsight[];
  optimalRanges?: OptimalRange[];
}

const PATTERN_ICONS: Record<string, string> = {
  poor_sleep_pattern: '😴',
  hormonal_fluctuation_pattern: '🌊',
  frequent_recovery_mode: '🛌',
  consistent_high_energy: '⚡',
  recurring_symptoms: '📋',
  balanced_pattern: '✨',
  insufficient_data: '📊'
};

const PatternInsights: React.FC<PatternInsightsProps> = ({
  patterns,
  dataPoints,
  correlations = [],
  optimalRanges = []
}) => {
  // Check for insufficient data
  const hasInsufficientData = dataPoints < 14;

  const getConfidenceBadge = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'medium':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'low':
        return 'bg-amber-100 text-amber-800 border-amber-300';
      default:
        return 'bg-neutral-100 text-neutral-800 border-neutral-300';
    }
  };

  if (hasInsufficientData) {
    return (
      <div className="card shadow-elegant">
        <h3 className="text-2xl font-serif font-bold text-neutral-900 mb-2">
          🔍 Pattern Insights
        </h3>
        <p className="text-neutral-600 mb-6">
          Build your wellness data history to unlock personalized insights
        </p>

        <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-xl p-8 text-center">
          <div className="text-6xl mb-4">📊</div>
          <h4 className="text-xl font-serif font-semibold text-neutral-900 mb-3">
            Not Enough Data Yet
          </h4>
          <p className="text-neutral-700 leading-relaxed mb-6 max-w-lg mx-auto">
            You have {dataPoints} check-in{dataPoints !== 1 ? 's' : ''}. Complete at least 14 daily
            check-ins to unlock pattern detection and personalized insights.
          </p>
          <div className="bg-white rounded-xl p-6 inline-block shadow-soft">
            <div className="flex flex-col items-center space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-48 bg-neutral-200 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((dataPoints / 14) * 100, 100)}%` }}
                  ></div>
                </div>
                <span className="text-lg font-bold text-neutral-900">
                  {dataPoints}/14
                </span>
              </div>
              <p className="text-sm text-neutral-600">
                Keep checking in daily to build your wellness data history
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Correlations Section */}
      {correlations && correlations.length > 0 && (
        <div className="card shadow-elegant">
          <h3 className="text-2xl font-serif font-bold text-neutral-900 mb-2">
            📊 Metric Correlations
          </h3>
          <p className="text-neutral-600 mb-6">
            Statistical relationships between your wellness metrics
          </p>

          <div className="space-y-6">
            {correlations.map((correlation, index) => (
              <CorrelationScatterPlot key={index} correlation={correlation} />
            ))}
          </div>
        </div>
      )}

      {/* Optimal Ranges Section */}
      {optimalRanges && optimalRanges.length > 0 && (
        <div className="card shadow-elegant">
          <h3 className="text-2xl font-serif font-bold text-neutral-900 mb-2">
            🎯 Your Optimal Ranges
          </h3>
          <p className="text-neutral-600 mb-6">
            Ideal metric ranges based on your best-performing days
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {optimalRanges.map((range, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-primary-50 to-white border border-primary-200 rounded-xl p-5 hover:shadow-soft transition-shadow duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-neutral-900 capitalize mb-1">
                      {range.metric.replace(/_/g, ' ')}
                    </h4>
                    <span className={`inline-block px-3 py-1 rounded-lg border text-xs font-semibold ${getConfidenceBadge(range.confidence)}`}>
                      {range.confidence.toUpperCase()} CONFIDENCE
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-neutral-600 mb-1">Optimal Range</div>
                    <div className="text-2xl font-bold text-primary-700">
                      {range.optimal_min.toFixed(1)}-{range.optimal_max.toFixed(1)}
                    </div>
                    {range.unit && (
                      <div className="text-xs text-neutral-500">{range.unit}</div>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 mb-4 border border-neutral-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-neutral-600">Your Current Average</span>
                    <span className="text-lg font-bold text-neutral-900">
                      {range.current_average.toFixed(1)}
                    </span>
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-2 relative overflow-hidden">
                    <div
                      className="absolute h-2 bg-primary-200"
                      style={{
                        left: `${((range.optimal_min / (range.optimal_max * 1.5)) * 100)}%`,
                        width: `${(((range.optimal_max - range.optimal_min) / (range.optimal_max * 1.5)) * 100)}%`
                      }}
                    ></div>
                    <div
                      className="absolute h-2 w-1 bg-primary-700"
                      style={{
                        left: `${((range.current_average / (range.optimal_max * 1.5)) * 100)}%`
                      }}
                    ></div>
                  </div>
                  {Math.abs(range.deviation) > 5 && (
                    <div className="text-xs text-neutral-600 mt-2">
                      {range.deviation > 0 ? '+' : ''}{range.deviation.toFixed(1)}% from optimal
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-neutral-800 leading-relaxed">
                    {range.recommendation}
                  </p>
                  <p className="text-xs text-neutral-600 mt-2">
                    Based on {range.sample_size} high-{range.based_on_metric.replace(/_/g, ' ')} days
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pattern Insights Section */}
      {patterns && patterns.length > 0 && (
        <div className="card shadow-elegant">
          <h3 className="text-2xl font-serif font-bold text-neutral-900 mb-2">
            🔍 Detected Patterns
          </h3>
          <p className="text-neutral-600 mb-6">
            Insights from {dataPoints} check-ins analyzed
          </p>

          <div className="space-y-4">
            {patterns.map((pattern, index) => {
              const icon = PATTERN_ICONS[pattern.type] || '💡';

              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-neutral-50 to-white border border-neutral-200 rounded-xl p-5 hover:shadow-soft transition-shadow duration-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="text-4xl flex-shrink-0">{icon}</div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="text-lg font-semibold text-neutral-900 leading-snug">
                          {pattern.description}
                        </h4>
                        <span className={`px-3 py-1 rounded-lg border text-xs font-semibold whitespace-nowrap ml-3 ${getConfidenceBadge(pattern.confidence)}`}>
                          {pattern.confidence.toUpperCase()}
                        </span>
                      </div>

                      <p className="text-xs text-neutral-500">
                        Detected on {new Date(pattern.detected_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {patterns.length === 0 && correlations.length === 0 && optimalRanges.length === 0 && (
        <div className="card shadow-elegant text-center py-12">
          <div className="text-6xl mb-4">✨</div>
          <h4 className="text-xl font-serif font-semibold text-neutral-900 mb-3">
            Keep Tracking!
          </h4>
          <p className="text-lg text-neutral-600 max-w-md mx-auto leading-relaxed">
            Continue your daily check-ins to unlock personalized insights and patterns
          </p>
        </div>
      )}
    </div>
  );
};

export default PatternInsights;
